import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { ErrorResponse } from '@core/error/error-response.model';
import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { CustomRxState } from '@core/state/rx.state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { TariffService } from '@api/services/tariff.service';
import { TariffGroupRep } from '@api/models/TariffEditorApi/Contract/tariff-group-rep';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { tap } from 'rxjs/operators';
import { GetTariffGroupRepDataResponse } from '@api/models/TariffEditorApi/Contract/get-tariff-group-rep-data-response';
import { TariffGroupsService } from '@api/services/tariff-groups.service';
import { AddTariffGroupRepRequest } from '@api/models/TariffEditorApi/Contract/add-tariff-group-rep-request';
import { AddTariffGroupRepResponse } from '@api/models/TariffEditorApi/Contract/add-tariff-group-rep-response';
import { SetTariffGroupRepRequest } from '@api/models/TariffEditorApi/Contract/set-tariff-group-rep-request';


interface IReportGroupsState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    reportGroups: TariffGroupRep[];

}

interface ReportGroupsActions {
    requestReportGroups: TariffGroupRep[];
}

@Injectable()
export class ReportGroupsState extends CustomRxState<IReportGroupsState> {
    readonly #actions: RxActions<ReportGroupsActions> = new RxActionFactory<ReportGroupsActions>().create();

    readonly #tariffService: TariffService = inject(TariffService);
    readonly #tariffGroupsService: TariffGroupsService = inject(TariffGroupsService);

    public readonly reportGroups$: Observable<TariffGroupRep[]> = this.select('reportGroups');
    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestReportGroups();
    }

    public requestReportGroups(): void {
        this.hold(
            this.#tariffService.apiTariffListTariffGroupRepGet(),
            this.#actions.requestReportGroups,
        );
    }

    public createReportGroup(body: AddTariffGroupRepRequest): Observable<AddTariffGroupRepResponse> {
        return this.#tariffGroupsService.apiTariffAddTariffGroupRepPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditReportGroup),
            tap(() => this.requestReportGroups()),
        );
    }

    public editReportGroup(body: SetTariffGroupRepRequest): Observable<void> {
        return this.#tariffGroupsService.apiTariffSetTariffGroupRepPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditReportGroup),
            tap(() => this.requestReportGroups()),
        );
    }

    public deleteReportGroup(trgId: number): Observable<void> {
        return this.#tariffGroupsService.apiTariffRemoveTariffGroupRepDelete({ trgId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteReportGroup),
            tap(() => this.requestReportGroups()),
        );
    }

    public getDetailDataReportGroup(trgId: number): Observable<GetTariffGroupRepDataResponse> {
        return this.#tariffService.apiTariffGetTariffGroupRepDataGet({ trgId });
    }

    private connectSelectors(): void {
        this.connect('reportGroups', this.#actions.requestReportGroups$);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            reportGroups: null,
        });
    }
}
